import { useEffect, useState } from 'react';
import {
  IconDown,
  IconLoader,
  IconTrash,
  IconUp,
  OnSelectChange,
  SelectSize,
  Textarea,
  Checkbox,
  InputText,
  InputSize,
  Autocomplete,
} from '@ui/src';
import {
  CRSAndFatcaFormValues,
  NewApplicationStatus,
} from '../../../../hoc/new-application/types';
import InfoRow from '../../info-row';
import { CRSAndFatcaTypes } from './types';

import cn from '../../new-application.module.css';

const CRSAndFatca = ({
  status,
  isEditable,
  deleteCrsAndFatcaInProgress,
  onDeleteCRSAndFatca,
  selectedCrsAndFatcaId,
  crsAndFatca,
  setCrsAndFatcaValues,
  crsAndFatcaValues,
  index,
  countries,
}: CRSAndFatcaTypes) => {
  const [formValues, setFormValues] = useState<CRSAndFatcaFormValues>();
  const [expanded, setExpanded] = useState(true);
  const [haveTin, setHaveTin] = useState(false);

  const onChange = (
    e: React.ChangeEvent<HTMLInputElement> | OnSelectChange,
  ) => {
    const nextFormValues = { ...formValues };

    const nextValues = crsAndFatcaValues?.map((item) => {
      const result = { ...item };

      if (nextFormValues.id === item.id) {
        if (e.target.name === `residenceCountryId-${item.id}`) {
          nextFormValues.residenceCountryId = e.target.value as string;
          result.residenceCountryId = e.target.value as string;
        } else if (e.target.name === `taxIdentificationNumber-${item.id}`) {
          nextFormValues.taxIdentificationNumber = e.target.value as string;
          result.taxIdentificationNumber = e.target.value as string;
        } else if (e.target.name === `noTinReason-${item.id}`) {
          nextFormValues.noTinReason = e.target.value as string;
          result.noTinReason = e.target.value as string;
        }
      }

      return result;
    });

    setFormValues(nextFormValues as CRSAndFatcaFormValues);
    setCrsAndFatcaValues(nextValues as CRSAndFatcaFormValues[]);
  };

  const onChangeHaveTin = (checked: boolean) => {
    setHaveTin(checked);

    const nextFormValues = { ...formValues };

    const nextValues = crsAndFatcaValues?.map((item) => {
      const result = { ...item };

      if (nextFormValues.id === item.id) {
        if (checked) {
          nextFormValues.noTinReason = '';
          result.noTinReason = '';
        } else {
          nextFormValues.taxIdentificationNumber = '';
          result.taxIdentificationNumber = '';
        }
      }

      return result;
    });

    setFormValues(nextFormValues as CRSAndFatcaFormValues);
    setCrsAndFatcaValues(nextValues as CRSAndFatcaFormValues[]);
  };

  useEffect(() => {
    if (crsAndFatca) {
      setFormValues(crsAndFatca);
    }
  }, [crsAndFatca]);

  useEffect(() => {
    if (formValues?.taxIdentificationNumber) {
      setHaveTin(true);
    }
  }, [formValues]);

  return (
    <div
      className={`${cn.accountCurrencyWrapper} ${!expanded ? cn.collapsed : ''}`}
    >
      <div
        className={`${cn.accountCurrencyTopWrapper} ${!expanded ? cn.collapsed : ''}`}
      >
        <div className={cn.accountCurrencyTop}>
          <button
            type="button"
            className={cn.accountCurrencyTopLeft}
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? (
              <IconUp width={14} height={14} />
            ) : (
              <IconDown height={14} width={14} />
            )}
            {`Tax residence #${index + 1}`}
          </button>
          {isEditable &&
            status !== NewApplicationStatus.ACCEPTED &&
            status !== NewApplicationStatus.DECLINED && (
              <button
                type="button"
                className={cn.deleteAccountCurrencyButton}
                onClick={() => onDeleteCRSAndFatca(crsAndFatca?.id as string)}
              >
                {deleteCrsAndFatcaInProgress &&
                selectedCrsAndFatcaId === crsAndFatca?.id ? (
                  <IconLoader width={18} height={18} />
                ) : (
                  <IconTrash />
                )}
              </button>
            )}
        </div>
      </div>
      {expanded && (
        <>
          <InfoRow title="Country" lessPadding>
            <Autocomplete
              name={`residenceCountryId-${crsAndFatca?.id}`}
              placeholder="Select country..."
              list={countries}
              value={formValues?.residenceCountryId}
              size={SelectSize.SMALL}
              onChange={(e) =>
                onChange({
                  target: {
                    name: `residenceCountryId-${crsAndFatca?.id}`,
                    value: e.target.value as string,
                  },
                })
              }
              selectWrapperClassName={
                status === NewApplicationStatus.ACCEPTED ||
                status === NewApplicationStatus.DECLINED
                  ? cn.selectDisabled
                  : ''
              }
              disabled={
                !isEditable ||
                Boolean(
                  status === NewApplicationStatus.ACCEPTED ||
                  status === NewApplicationStatus.DECLINED,
                )
              }
              tabIndex={
                !isEditable &&
                Boolean(
                  status === NewApplicationStatus.ACCEPTED ||
                  status === NewApplicationStatus.DECLINED,
                )
                  ? -1
                  : 0
              }
              borderless
              fullWidth
            />
          </InfoRow>
          <div className={cn.tinCheckboxWrapper}>
            <Checkbox
              name={`haveTin-${crsAndFatca?.id}`}
              type="checkbox"
              checked={haveTin}
              onChange={(e) => onChangeHaveTin(e.target.checked)}
              title="Having a TIN"
              disabled={
                !isEditable ||
                Boolean(
                  status === NewApplicationStatus.ACCEPTED ||
                  status === NewApplicationStatus.DECLINED,
                )
              }
              tabIndex={
                !isEditable &&
                Boolean(
                  status === NewApplicationStatus.ACCEPTED ||
                  status === NewApplicationStatus.DECLINED,
                )
                  ? -1
                  : 0
              }
              labelClassName={cn.addressCheckbox}
            />
          </div>
          {haveTin && (
            <InfoRow title="TIN" lessPadding>
              <InputText
                name={`taxIdentificationNumber-${crsAndFatca?.id}`}
                placeholder="Select TIN..."
                value={formValues?.taxIdentificationNumber}
                size={InputSize.SMALL}
                onChange={(e) => {
                  onChange({
                    target: {
                      name: `taxIdentificationNumber-${crsAndFatca?.id}`,
                      value: e.target.value,
                    },
                  });
                }}
                disabled={
                  !isEditable ||
                  Boolean(
                    status === NewApplicationStatus.ACCEPTED ||
                    status === NewApplicationStatus.DECLINED,
                  )
                }
                tabIndex={
                  !isEditable &&
                  Boolean(
                    status === NewApplicationStatus.ACCEPTED ||
                    status === NewApplicationStatus.DECLINED,
                  )
                    ? -1
                    : 0
                }
                className="borderless"
              />
            </InfoRow>
          )}
          {!haveTin && (
            <InfoRow
              title="Reason for no TIN"
              className={cn.noFlex}
              lessPadding
            >
              <Textarea
                name={`noTinReason-${crsAndFatca?.id}`}
                placeholder="Enter description..."
                value={formValues?.noTinReason}
                onChange={(e) => {
                  onChange({
                    target: {
                      name: `noTinReason-${crsAndFatca?.id}`,
                      value: e.target.value,
                    },
                  });
                }}
                className={`borderless ${cn.pepTextarea} ${cn.stepOneTextarea}`}
                tabIndex={
                  !isEditable &&
                  Boolean(
                    status === NewApplicationStatus.ACCEPTED ||
                    status === NewApplicationStatus.DECLINED,
                  )
                    ? -1
                    : 0
                }
                disabled={
                  !isEditable ||
                  Boolean(
                    status === NewApplicationStatus.ACCEPTED ||
                    status === NewApplicationStatus.DECLINED,
                  )
                }
              />
            </InfoRow>
          )}
        </>
      )}
    </div>
  );
};

export default CRSAndFatca;
